/**
 * Work long-reply scroll policy.
 *
 * Normal chat follows the live tail so new tokens stay in view. Work used the
 * same pin, but a paced essay plus the plan / live-status rows under the reply
 * glued the viewport to the bottom of a multi-thousand-pixel message and shoved
 * the reader down the page. Once a work reply has streaming text, freeze on
 * that reply's start and let tokens grow below — the same "stream in place"
 * feel as chat. Tool-only activity (no text yet) still follows the tail so
 * the live status stays visible.
 */

export const WORK_STREAM_SCROLL = {
  TAIL: 'tail',
  START: 'start',
};

export function findStreamingWorkAssistant(messages) {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message?.role !== 'assistant') continue;
    if (!(message.isStreaming || message.isTyping)) continue;
    const content = String(message.content || '');
    if (!content.trim()) continue;
    return { id: message.id || message._id || null, length: content.length };
  }
  return null;
}

export function getWorkStreamScrollMode({ isCoworkerChat, hasStreamingText, followLive } = {}) {
  if (!isCoworkerChat) return WORK_STREAM_SCROLL.TAIL;
  if (hasStreamingText && !followLive) return WORK_STREAM_SCROLL.START;
  return WORK_STREAM_SCROLL.TAIL;
}

/**
 * Align `messageClientTop` with the scroller's top, leaving `padding` px.
 * Returns the next scrollTop (clamped at 0).
 */
export function nextScrollTopToAlignStart({
  scrollerScrollTop = 0,
  scrollerClientTop = 0,
  messageClientTop = 0,
  padding = 12,
} = {}) {
  return Math.max(0, scrollerScrollTop + (messageClientTop - scrollerClientTop) - padding);
}

/**
 * Jump-to-live / scroll-to-bottom is normally toggled from scroll events.
 * Work in-place streams grow content without moving scrollTop, so the button
 * must also be derived from content-size changes or it stays invisible.
 */
/**
 * Studio open remounts / reflows the chat column as stills and clips land.
 * RN Web FlatList then snaps scrollTop to 0. Restore the last good offset
 * unless the user is navigating the thread — especially scrolling down from
 * the top. Restoring during that gesture pins them on older rows and the
 * list never grows far enough to reach the latest messages.
 */
export function shouldRestoreStudioScroll({
  previousOffset = 0,
  nextOffset = 0,
  userScrolling = false,
  userIntentUntil = 0,
  now = Date.now(),
  lastDownwardAt = 0,
  pinningToBottom = false,
} = {}) {
  if (userScrolling) return false;
  if (pinningToBottom) return false;
  if (now < Number(userIntentUntil || 0)) return false;
  if (now - Number(lastDownwardAt || 0) < 2500) return false;
  if (previousOffset < 80) return false;
  return previousOffset > 80 && nextOffset < 32;
}

export function shouldShowScrollToBottomButton({
  contentHeight = 0,
  layoutHeight = 0,
  scrollTop = 0,
  bottomThreshold = 100,
} = {}) {
  const canScroll = contentHeight > layoutHeight + 20;
  if (!canScroll) return false;
  return scrollTop + layoutHeight < contentHeight - bottomThreshold;
}
